/**
 * BankTransferForm - Bank transfer form component
 */
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BANK_COUNTRIES } from './constants';

const BankTransferForm = ({ bankDetails, setBankDetails }) => {
  const updateField = (field, value) => {
    setBankDetails({ ...bankDetails, [field]: value });
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2 col-span-2">
          <Label htmlFor="account-holder">Account Holder Name *</Label>
          <Input
            id="account-holder"
            placeholder="John Doe"
            value={bankDetails.account_holder_name}
            onChange={(e) => updateField('account_holder_name', e.target.value)}
            className="h-12 rounded-xl"
            data-testid="account-holder-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="bank-country">Bank Country *</Label>
          <Select 
            value={bankDetails.bank_country} 
            onValueChange={(v) => updateField('bank_country', v)}
          >
            <SelectTrigger className="h-12 rounded-xl" data-testid="bank-country-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BANK_COUNTRIES.map((country) => (
                <SelectItem key={country.code} value={country.code}>
                  {country.flag} {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="bank-name">Bank Name *</Label>
          <Input
            id="bank-name"
            placeholder="Bank of America"
            value={bankDetails.bank_name}
            onChange={(e) => updateField('bank_name', e.target.value)}
            className="h-12 rounded-xl"
            data-testid="bank-name-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="account-number">Account Number *</Label>
          <Input
            id="account-number"
            placeholder="1234567890"
            value={bankDetails.account_number}
            onChange={(e) => updateField('account_number', e.target.value)}
            className="h-12 rounded-xl font-mono"
            data-testid="account-number-input"
          />
        </div>
        
        {bankDetails.bank_country === 'US' ? (
          <div className="space-y-2">
            <Label htmlFor="routing-number">Routing Number *</Label>
            <Input
              id="routing-number"
              placeholder="021000021"
              value={bankDetails.routing_number}
              onChange={(e) => updateField('routing_number', e.target.value)}
              className="h-12 rounded-xl font-mono"
              maxLength={9}
              data-testid="routing-number-input"
            />
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                placeholder="DE89370400440532013000"
                value={bankDetails.iban}
                onChange={(e) => updateField('iban', e.target.value.toUpperCase())}
                className="h-12 rounded-xl font-mono"
                data-testid="iban-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="swift-code">SWIFT/BIC Code</Label>
              <Input
                id="swift-code"
                placeholder="COBADEFFXXX"
                value={bankDetails.swift_code}
                onChange={(e) => updateField('swift_code', e.target.value.toUpperCase())}
                className="h-12 rounded-xl font-mono"
                maxLength={11}
                data-testid="swift-code-input"
              />
            </div>
          </>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="account-type">Account Type</Label>
          <Select 
            value={bankDetails.account_type} 
            onValueChange={(v) => updateField('account_type', v)}
          >
            <SelectTrigger className="h-12 rounded-xl" data-testid="account-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="checking">Checking</SelectItem>
              <SelectItem value="savings">Savings</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="p-3 rounded-xl bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm">
        <p className="font-medium">Bank Transfer Timeline</p>
        <p className="text-xs mt-1">
          {bankDetails.bank_country === 'US' 
            ? 'US transfers: 1-3 business days' 
            : 'International transfers: 3-5 business days'}
        </p>
      </div>
    </>
  );
};

export default BankTransferForm;
